import Scanner, { isSpace, isQuote } from '@emmetio/scanner';

/**
 * Performs fast scan of given stylesheet (CSS, LESS, SCSS) source code and runs
 * callback for each token and its range found. The goal of this parser is to quickly
 * determine document structure: selector, property, value and block end.
 * It doesn’t provide detailed info about CSS atoms like compound selectors,
 * operators, quoted string etc. to reduce memory allocations: this data can be
 * parsed later on demand.
 */
function scan(source, callback) {
    const scanner = new Scanner(source);
    const state = {
        start: -1,
        end: -1,
        propertyStart: -1,
        propertyEnd: -1,
        propertyDelimiter: -1,
        expression: 0,
    };
    let blockEnd;
    const notify = (type, delimiter = scanner.start, start = state.start, end = state.end) => {
        return callback(type, start, end, delimiter) === false;
    };
    while (!scanner.eof()) {
        if (comment(scanner) || whitespace(scanner)) {
            continue;
        }
        scanner.start = scanner.pos;
        if ((blockEnd = scanner.eat(125 /* RightCurly */)) || scanner.eat(59 /* Semicolon */)) {
            // Block or property end
            if (state.propertyStart !== -1) {
                // We have pending property
                if (notify("propertyName" /* PropertyName */, state.propertyDelimiter, state.propertyStart, state.propertyEnd)) {
                    return;
                }
                if (state.start === -1) {
                    // Explicit property value state: emit empty value
                    state.start = state.end = scanner.start;
                }
                if (notify("propertyValue" /* PropertyValue */)) {
                    return;
                }
            }
            else if (state.start !== -1 && notify("propertyName" /* PropertyName */)) {
                // Flush consumed token
                return;
            }
            if (blockEnd) {
                state.start = scanner.start;
                state.end = scanner.pos;
                if (notify("blockEnd" /* BlockEnd */)) {
                    return;
                }
            }
            reset(state);
        }
        else if (scanner.eat(123 /* LeftCurly */)) {
            // Block start
            if (state.start === -1 && state.propertyStart === -1) {
                // No consumed selector, emit empty value as selector start
                state.start = state.end = scanner.pos;
            }
            if (state.propertyStart !== -1) {
                // Now we know that value that looks like property name-value pair
                // was actually a selector
                state.start = state.propertyStart;
            }
            if (notify("selector" /* Selector */)) {
                return;
            }
            reset(state);
        }
        else if (scanner.eat(58 /* Colon */) && !isKnownSelectorColon(scanner, state)) {
            // Colon could be one of the following:
            // — property delimiter: `foo: bar`, must be in block context
            // — variable delimiter: `$foo: bar`, could be anywhere
            // — pseudo-selector: `a:hover`, could be anywhere (for LESS and SCSS)
            // — media query expression: `min-width: 100px`, must be inside expression context
            // Since I can’t easily detect `:` meaning for sure, we’ll update state
            // to accumulate possible property name-value pair or selector
            if (state.propertyStart === -1) {
                state.propertyStart = state.start;
            }
            state.propertyEnd = state.end;
            state.propertyDelimiter = scanner.pos - 1;
            state.start = state.end = -1;
        }
        else {
            if (state.start === -1) {
                state.start = scanner.pos;
            }
            if (scanner.eat(40 /* LeftRound */)) {
                state.expression++;
            }
            else if (scanner.eat(41 /* RightRound */)) {
                state.expression--;
            }
            else if (!literal(scanner)) {
                scanner.pos++;
            }
            state.end = scanner.pos;
        }
    }
    if (state.propertyStart !== -1) {
        // Pending property name
        if (notify("propertyName" /* PropertyName */, state.propertyDelimiter, state.propertyStart, state.propertyEnd)) {
            return;
        }
    }
    if (state.start !== -1) {
        // There’s pending token in state
        notify(state.propertyStart !== -1 ? "propertyValue" /* PropertyValue */ : "propertyName" /* PropertyName */, -1);
    }
}
function whitespace(scanner) {
    return scanner.eatWhile(isSpace);
}
/**
 * Consumes CSS comments from scanner: `/*  * /`
 * It’s possible that comment may not have closing part
 */
function comment(scanner) {
    const start = scanner.pos;
    if (scanner.eat(47 /* Slash */) && scanner.eat(42 /* Asterisk */)) {
        scanner.start = start;
        while (!scanner.eof()) {
            if (scanner.eat(42 /* Asterisk */)) {
                if (scanner.eat(47 /* Slash */)) {
                    return true;
                }
                continue;
            }
            scanner.pos++;
        }
        return true;
    }
    else {
        scanner.pos = start;
    }
    return false;
}
/**
 * Consumes single- or double-quoted string literal
 */
function literal(scanner) {
    const ch = scanner.peek();
    if (isQuote(ch)) {
        scanner.start = scanner.pos++;
        while (!scanner.eof()) {
            if (scanner.eat(ch) || scanner.eat(10 /* LF */) || scanner.eat(13 /* CR */)) {
                break;
            }
            // Skip escape character, if any
            scanner.eat(92 /* Backslash */);
            scanner.pos++;
        }
        // Do not throw if string is incomplete
        return true;
    }
}
function reset(state) {
    state.start = state.end = state.propertyStart = state.propertyEnd = state.propertyDelimiter = -1;
}
/**
 * Check if current state is a known selector context for `:` delimiter
 */
function isKnownSelectorColon(scanner, state) {
    // Either inside expression like `(min-width: 10px)` or pseudo-element `::before`
    return state.expression || scanner.eatWhile(58 /* Colon */);
}

// NB: no `Minus` operator, it must be handled differently
const operators = [
    43 /* Plus */, 47 /* Division */, 42 /* Multiplication */,
    44 /* Comma */
];
/**
 * Splits given CSS value into token list
 */
function splitValue(value, offset = 0) {
    let start = -1;
    let expression = 0;
    let pos = 0;
    const result = [];
    const scanner = new Scanner(value);
    while (!scanner.eof()) {
        pos = scanner.pos;
        if (scanner.eat(isSpace) || scanner.eat(isOperator) || isMinusOperator(scanner)) {
            // Use space as value delimiter but only if not in expression context,
            // e.g. `1 2` are distinct values but `(1 2)` not
            if (!expression && start !== -1) {
                result.push([offset + start, offset + pos]);
                start = -1;
            }
            scanner.eatWhile(isSpace);
        }
        else {
            if (start === -1) {
                start = scanner.pos;
            }
            if (scanner.eat(40 /* LeftRound */)) {
                expression++;
            }
            else if (scanner.eat(41 /* RightRound */)) {
                expression--;
            }
            else if (!literal(scanner)) {
                scanner.pos++;
            }
        }
    }
    if (start !== -1 && start !== scanner.pos) {
        result.push([offset + start, offset + scanner.pos]);
    }
    return result;
}
function isOperator(ch) {
    return operators.includes(ch);
}
/**
 * Check if current scanner state is at minus operator
 */
function isMinusOperator(scanner) {
    // Minus operator is tricky since CSS supports dashes in keyword names like
    // `no-repeat`
    const start = scanner.pos;
    if (scanner.eat(45 /* Minus */) && scanner.eat(isSpace)) {
        return true;
    }
    scanner.pos = start;
    return false;
}

function match(source, pos) {
    const pool = [];
    const stack = [];
    let result = null;
    let pendingProperty = null;
    const releasePending = () => {
        if (pendingProperty) {
            releaseRange(pool, pendingProperty);
            pendingProperty = null;
        }
    };
    scan(source, (type, start, end, delimiter) => {
        if (type === "selector" /* Selector */) {
            releasePending();
            stack.push(allocRange(pool, start, end, delimiter));
        }
        else if (type === "blockEnd" /* BlockEnd */) {
            releasePending();
            const parent = stack.pop();
            if (parent && parent[0] < pos && pos < end) {
                result = {
                    type: 'selector',
                    start: parent[0],
                    end,
                    bodyStart: parent[2] + 1,
                    bodyEnd: start
                };
                return false;
            }
        }
        else if (type === "propertyName" /* PropertyName */) {
            releasePending();
            pendingProperty = allocRange(pool, start, end, delimiter);
        }
        else if (type === "propertyValue" /* PropertyValue */) {
            if (pendingProperty && pendingProperty[0] < pos && pos < end) {
                result = {
                    type: 'property',
                    start: pendingProperty[0],
                    end: delimiter + 1,
                    bodyStart: start,
                    bodyEnd: end
                };
                return false;
            }
            releasePending();
        }
    });
    return result;
}
/**
 * Returns balanced CSS model: a list of all ranges that could possibly match
 * given location when moving in outward direction
 */
function balancedOutward(source, pos) {
    const pool = [];
    const stack = [];
    const result = [];
    let property = null;
    scan(source, (type, start, end, delimiter) => {
        if (type === "selector" /* Selector */) {
            stack.push(allocRange(pool, start, end, delimiter));
        }
        else if (type === "blockEnd" /* BlockEnd */) {
            const left = stack.pop();
            if (left && left[0] < pos && end > pos) {
                // Matching section found
                const inner = innerRange(source, left[2] + 1, start);
                inner && push(result, inner);
                push(result, [left[0], end]);
            }
            left && releaseRange(pool, left);
            if (!stack.length) {
                return false;
            }
        }
        else if (type === "propertyName" /* PropertyName */) {
            property && releaseRange(pool, property);
            property = allocRange(pool, start, end, delimiter);
        }
        else if (type === "propertyValue" /* PropertyValue */) {
            if (property && property[0] < pos && Math.max(delimiter, end) > pos) {
                // Push full token and value range
                push(result, [start, end]);
                push(result, [property[0], delimiter !== -1 ? delimiter + 1 : end]);
            }
        }
        if (type !== "propertyName" /* PropertyName */ && property) {
            releaseRange(pool, property);
            property = null;
        }
    });
    return result;
}
/**
 * Returns balanced CSS selectors: a list of all ranges that could possibly match
 * given location when moving in inward direction
 */
function balancedInward(source, pos) {
    // Collecting ranges for inward balancing is a bit trickier: we have to store
    // first child of every matched selector until we find the one that matches given
    // location
    const pool = [];
    const stack = [];
    const result = [];
    let pendingProperty = null;
    const alloc = (start, end, delimiter) => {
        if (pool.length) {
            const range = pool.pop();
            range.start = start;
            range.end = end;
            range.delimiter = delimiter;
            return range;
        }
        return { start, end, delimiter, firstChild: null };
    };
    const release = (range) => {
        range.firstChild = null;
        pool.push(range);
    };
    const releasePending = () => {
        if (pendingProperty) {
            release(pendingProperty);
            pendingProperty = null;
        }
    };
    /**
     * Pushes given inward range as a first child of current selector only if it’s
     * not set yet
     */
    const pushChild = (start, end, delimiter) => {
        const parent = last(stack);
        if (parent && !parent.firstChild) {
            parent.firstChild = alloc(start, end, delimiter);
        }
    };
    scan(source, (type, start, end, delimiter) => {
        if (type === "blockEnd" /* BlockEnd */) {
            releasePending();
            let range = stack.pop();
            if (!range) {
                // Some sort of lone closing brace, ignore it
                return;
            }
            if (range.start <= pos && pos <= end) {
                // Matching selector found: add it and its inner range into result
                let inner = innerRange(source, range.delimiter + 1, start);
                push(result, [range.start, end]);
                inner && push(result, inner);
                while (range.firstChild) {
                    const child = range.firstChild;
                    inner = innerRange(source, child.delimiter + 1, child.end - 1);
                    push(result, [child.start, child.end]);
                    inner && push(result, inner);
                    range = child;
                }
                return false;
            }
            else {
                const parent = last(stack);
                if (parent && !parent.firstChild) {
                    // No first child in parent node: store current selector
                    range.end = end;
                    parent.firstChild = range;
                }
                else {
                    release(range);
                }
            }
        }
        else if (type === "propertyName" /* PropertyName */) {
            releasePending();
            pendingProperty = alloc(start, end, delimiter);
            pushChild(start, end, delimiter);
        }
        else if (type === "propertyValue" /* PropertyValue */) {
            if (pendingProperty) {
                if (pendingProperty.start <= pos && end >= pos) {
                    // Direct hit into property, no need to look further
                    push(result, [pendingProperty.start, delimiter + 1]);
                    push(result, [start, end]);
                    releasePending();
                    return false;
                }
                const parent = last(stack);
                if (parent && parent.firstChild && parent.firstChild.start === pendingProperty.start) {
                    // First child is an expected property name, update its range
                    // to include property value
                    parent.firstChild.end = delimiter !== -1 ? delimiter + 1 : end;
                }
                releasePending();
            }
        }
        else {
            // Selector start
            stack.push(alloc(start, end, delimiter));
            releasePending();
        }
    });
    stack.length = pool.length = 0;
    return result;
}
/**
 * Returns inner range for given selector bounds: narrows it to first non-empty
 * region. If resulting region is empty, returns `null`
 */
function innerRange(source, start, end) {
    while (start < end && isSpace(source.charCodeAt(start))) {
        start++;
    }
    while (end > start && isSpace(source.charCodeAt(end - 1))) {
        end--;
    }
    return start !== end ? [start, end] : null;
}
function allocRange(pool, start, end, delimiter) {
    if (pool.length) {
        const range = pool.pop();
        range[0] = start;
        range[1] = end;
        range[2] = delimiter;
        return range;
    }
    return [start, end, delimiter];
}
function releaseRange(pool, range) {
    range && pool.push(range);
    return null;
}
function push(ranges, range) {
    const prev = ranges.length ? ranges[ranges.length - 1] : null;
    if ((!prev || prev[0] !== range[0] || prev[1] !== range[1]) && range[0] !== range[1]) {
        ranges.push(range);
    }
}
function last(arr) {
    return arr.length ? arr[arr.length - 1] : null;
}

export default match;
export { balancedInward, balancedOutward, scan, splitValue };
//# sourceMappingURL=css-matcher.es.js.map
